import React, { useState, useEffect, useMemo } from 'react';
import { 
  XMarkIcon, 
  CalendarIcon, 
  ExclamationTriangleIcon,
  DocumentTextIcon,
  UserIcon,
  ChevronUpDownIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Combobox } from '@headlessui/react';
import { CreateWarrantyClaimData, ClaimPriority, Warranty, WarrantyClaim } from '../../types/warranty';
import { useWarranties } from '../../hooks/useWarranties';
import Modal from '../common/Modal';

interface WarrantyClaimFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWarrantyClaimData) => Promise<void>;
  claim?: WarrantyClaim;
  loading?: boolean;
  organizationId: string;
}

const WarrantyClaimForm: React.FC<WarrantyClaimFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  claim,
  loading = false,
  organizationId
}) => {
  const { warranties } = useWarranties(organizationId);
  
  // Search states
  const [warrantyQuery, setWarrantyQuery] = useState('');

  const [formData, setFormData] = useState<CreateWarrantyClaimData>({
    warrantyId: '',
    description: '',
    priority: 'normal' as ClaimPriority,
    expectedResolutionDate: undefined,
    assignedTo: '',
    notes: ''
  });

  // Load claim data if editing
  useEffect(() => {
    if (claim && isOpen) {
      setFormData({
        warrantyId: claim.warrantyId,
        description: claim.description,
        priority: claim.priority,
        expectedResolutionDate: claim.expectedResolutionDate,
        assignedTo: claim.assignedTo || '',
        notes: claim.notes || ''
      });
    } else if (!claim && isOpen) {
      setFormData({
        warrantyId: '',
        description: '',
        priority: 'normal',
        expectedResolutionDate: undefined,
        assignedTo: '',
        notes: ''
      });
    }
  }, [claim, isOpen]);

  // Reset search queries when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setWarrantyQuery('');
    }
  }, [isOpen]);

  // Filtered warranties for search (only active warranties)
  const filteredWarranties = useMemo(() => {
    const activeWarranties = warranties.filter(w => w.status === 'active');
    
    if (!warrantyQuery) return activeWarranties.slice(0, 20);
    return activeWarranties.filter(warranty => {
      const searchLower = warrantyQuery.toLowerCase();
      return (
        warranty.warrantyNumber.toLowerCase().includes(searchLower) ||
        warranty.productName.toLowerCase().includes(searchLower) ||
        warranty.customerName.toLowerCase().includes(searchLower) ||
        warranty.productCode?.toLowerCase().includes(searchLower)
      );
    }).slice(0, 20);
  }, [warranties, warrantyQuery]);

  const selectedWarranty = warranties.find(w => w.id === formData.warrantyId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.warrantyId) {
      alert('Kérjük válasszon garanciát!');
      return;
    }
    
    if (!formData.description.trim()) {
      alert('Kérjük adja meg a probléma leírását!');
      return;
    }
    
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error submitting warranty claim form:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDateChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value ? new Date(value) : undefined
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={claim ? 'Garanciális eset szerkesztése' : 'Új garanciális eset'}
      size="4xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-orange-500 to-red-600">
              <ExclamationTriangleIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Alapadatok</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Warranty Selection */}
              <div className="col-span-12">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Garancia *
                </label>
                <Combobox
                  value={formData.warrantyId}
                  onChange={(warrantyId) => setFormData(prev => ({ ...prev, warrantyId }))}
                >
                  <div className="relative">
                    <div className="relative w-full">
                      <Combobox.Input
                        className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                        onChange={event => setWarrantyQuery(event.target.value)}
                        onClick={() => setWarrantyQuery('')}
                        displayValue={(warrantyId: string) => {
                          const warranty = warranties.find(w => w.id === warrantyId);
                          return warranty ? `${warranty.warrantyNumber} - ${warranty.productName} (${warranty.customerName})` : '';
                        }}
                        placeholder="Garancia keresése..."
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    <Combobox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                      {filteredWarranties.length === 0 && warrantyQuery !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-500 dark:text-gray-400">
                          Nincs találat
                        </div>
                      ) : (
                        filteredWarranties.map(warranty => (
                          <Combobox.Option
                            key={warranty.id}
                            value={warranty.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            <div>
                              <div className="font-medium">{warranty.warrantyNumber}</div>
                              <div className="text-sm text-gray-600 dark:text-gray-400">{warranty.productName}</div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                Ügyfél: {warranty.customerName} | Lejár: {warranty.endDate.toLocaleDateString('hu-HU')}
                              </div>
                            </div>
                          </Combobox.Option>
                        ))
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </div>

              {/* Priority */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prioritás *
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  required
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                >
                  <option value="low">Alacsony</option>
                  <option value="normal">Normál</option>
                  <option value="high">Magas</option>
                  <option value="urgent">Sürgős</option>
                </select>
              </div>

              {/* Expected Resolution Date */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <ClockIcon className="w-4 h-4 inline mr-1" />
                  Várható megoldás dátuma
                </label>
                <input
                  type="date"
                  value={formData.expectedResolutionDate ? formData.expectedResolutionDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleDateChange('expectedResolutionDate', e.target.value)}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Problem Description */}
        <div className="mb-6">
          <div className="form-section-header flex items-center mb-3">
            <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-blue-500 to-indigo-600">
              <DocumentTextIcon className="h-4 w-4" />
            </div>
            <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Probléma leírása</h3>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-4">
            <div className="grid grid-cols-12 gap-4">
              {/* Description */}
              <div className="col-span-12">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Probléma részletes leírása *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="Írja le részletesen a problémát..."
                />
              </div>

              {/* Assigned To */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <UserIcon className="w-4 h-4 inline mr-1" />
                  Felelős személy
                </label>
                <input
                  type="text"
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="Ki foglalkozik az esettel?"
                />
              </div>

              {/* Notes */}
              <div className="col-span-12 sm:col-span-6">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Megjegyzések
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={3}
                  className="block w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm py-2 px-3 text-gray-800 dark:text-gray-200 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-primary-500 dark:focus:border-primary-400 transition-colors duration-200"
                  placeholder="További megjegyzések..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Selected Warranty Info */}
        {selectedWarranty && (
          <div className="mb-6">
            <div className="form-section-header flex items-center mb-3">
              <div className="text-white p-1.5 mr-2 rounded shadow-sm bg-gradient-to-r from-green-500 to-teal-600">
                <DocumentTextIcon className="h-4 w-4" />
              </div>
              <h3 className="text-gray-800 dark:text-gray-200 font-medium text-sm">Kiválasztott garancia részletei</h3>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 p-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Garancia száma:</span>
                  <p className="text-gray-900 dark:text-gray-100">{selectedWarranty.warrantyNumber}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Termék:</span>
                  <p className="text-gray-900 dark:text-gray-100">{selectedWarranty.productName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Ügyfél:</span>
                  <p className="text-gray-900 dark:text-gray-100">{selectedWarranty.customerName}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600 dark:text-gray-400">Lejárat:</span>
                  <p className="text-gray-900 dark:text-gray-100">{selectedWarranty.endDate.toLocaleDateString('hu-HU')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white rounded-lg text-sm hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors disabled:opacity-50"
          >
            {loading ? 'Mentés...' : (claim ? 'Frissítés' : 'Létrehozás')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WarrantyClaimForm; 